import React, { useEffect, useState, useCallback } from "react";
import { GATE_V2_STAGES } from "@/lib/gate";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import NewOrderForm from "@/components/NewOrderForm";
import OrderTable from "@/components/OrderTable";
import GlobalSearch from "@/components/GlobalSearch";
import OrderDetailDialog from "@/components/OrderDetailDialog";
import StatusBadge from "@/components/StatusBadge";
import RevenueTimelineChart from "@/components/RevenueTimelineChart";
import SalesOrdersDialog from "@/components/SalesOrdersDialog";
import ReceivablesDialog from "@/components/ReceivablesDialog";
import ReceivedPaymentsDialog from "@/components/ReceivedPaymentsDialog";
import SettingsPage from "@/pages/Settings";
import Sidebar from "@/components/Sidebar";
import StatusPipeline from "@/components/StatusPipeline";
import QuickPaymentDialog from "@/components/QuickPaymentDialog";
import GenerateQrDialog from "@/components/GenerateQrDialog";
import InstallAppButton from "@/components/InstallAppButton";
import OrderCard from "@/components/OrderCard";
import ReportsPage from "@/pages/Reports";
import OperatorsPage from "@/pages/Operators";
import DeliveryPanel from "@/pages/DeliveryPanel";
import PinLock, { lockAllSections } from "@/components/PinLock";
import ThemeToggle from "@/components/ThemeToggle";
import { HeyShobhit } from "@/components/assistant/HeyShobhit";
import EstimatesPage from "@/pages/Estimates";
import TasksPage from "@/pages/TasksPage";
import StaffPayrollPage from "@/pages/StaffPayrollPage";
import MyTasksBanner from "@/components/MyTasksBanner";
import RecycleBinPage from "@/pages/RecycleBin";
import ItemsPage from "@/pages/Items";
import WeldersPage from "@/pages/Welders";
import FabricationOverviewPage from "@/pages/FabricationOverview";
import PartiesPage from "@/pages/Parties";
import PaymentsPage from "@/pages/PaymentsPage";
import DailyExpensesPage from "@/pages/DailyExpensesPage";
import CommissionPage from "@/pages/CommissionPage";
import DesignsPage from "@/pages/DesignsPage";
import DuesPage from "@/pages/DuesPage";
import ThemePage from "@/pages/ThemePage";
import HomeDashboard from "@/components/home/HomeDashboard";
import WelderCommissionsPage from "@/pages/WelderCommissionsPage";
import AccountsPage from "@/pages/AccountsPage";
import {
  Plus, List, Money, Bank, Package,
  TrendUp, CurrencyInr, Fire, ArrowRight, Users as UsersIcon,
  ClipboardText, GridFour, ListBullets, CaretDown, QrCode
} from "@phosphor-icons/react";

const STAGE_TABS = [
  { v: "new_order", label: "New Order" },
  { v: "design_pending", label: "Design Pending" },
  { v: "ready_to_cut", label: "Ready to Cut" },
  { v: "in_cutting", label: "In Cutting" },
  { v: "completed", label: "Ready for Delivery" },
  { v: "delivered", label: "Delivered" },
  { v: "all", label: "All" },
  { v: "on_hold", label: "Hold" },
];

// Gate & Railings uses a different workflow — different stages after New Order
const GATE_STAGE_TABS = [
  ...GATE_V2_STAGES,
  { v: "all", label: "All" },
  { v: "on_hold", label: "Hold" },
];

const RANGE_OPTIONS = [
  { value: "this_month", label: "THIS MONTH" },
  { value: "last_month", label: "LAST MONTH" },
  { value: "quarter", label: "QUARTER" },
  { value: "last_6", label: "6 MONTHS" },
  { value: "year", label: "YEAR" },
];

const RANGE_LABEL = {
  this_month: "THIS MONTH",
  last_month: "LAST MONTH",
  quarter: "LAST QUARTER (3 MO)",
  last_6: "LAST 6 MONTHS",
  year: "LAST 12 MONTHS",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [section, setSection] = useState("home");
  const [stage, setStage] = useState("all");
  const [orders, setOrders] = useState([]);
  const [today, setToday] = useState(null);
  const [overview, setOverview] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [revenueRange, setRevenueRange] = useState("this_month");
  const [revenueTimeline, setRevenueTimeline] = useState(null);
  const [revenueTimelineWeek, setRevenueTimelineWeek] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [quickPayOpen, setQuickPayOpen] = useState(false);
  const [quickPayOrderId, setQuickPayOrderId] = useState(null);
  const [generateQrOpen, setGenerateQrOpen] = useState(false);
  const [salesBucket, setSalesBucket] = useState(null); // clicked bar → drilldown dialog
  const [receivablesOpen, setReceivablesOpen] = useState(false);
  const [receivedRange, setReceivedRange] = useState("today");
  const [receivedData, setReceivedData] = useState(null);
  const [receivedWeek, setReceivedWeek] = useState(null);
  const [receivedDialogOpen, setReceivedDialogOpen] = useState(false);

  const loadOrders = useCallback(async () => {
    const parts = [];
    if (stage !== "all") parts.push(`status=${stage === "design_cutting" ? "design_cutting,in_cutting" : stage}`);
    if (section === "gates") parts.push("project_type=gate_railing");
    else if (section === "orders") parts.push("project_type=sheet_cutting");
    const params = parts.length ? `?${parts.join("&")}` : "";
    const { data } = await api.get(`/orders${params}`);
    setOrders(data);
  }, [stage, section]);

  const loadStats = useCallback(async () => {
    try {
      const [t, o, m, l] = await Promise.all([
        api.get("/analytics/today"),
        api.get("/analytics/overview"),
        api.get("/analytics/monthly?months=6"),
        api.get("/analytics/leaderboard?limit=5"),
      ]);
      setToday(t.data); setOverview(o.data); setMonthly(m.data); setLeaderboard(l.data);
    } catch (_e) { /* ignore */ }
  }, []);

  const loadRevenueTimeline = useCallback(async () => {
    try {
      setRevenueTimeline(await fetchDeptTimelines(revenueRange));
    } catch (_e) { /* ignore */ }
  }, [revenueRange]);

  const loadReceived = useCallback(async () => {
    try {
      const { data } = await api.get(`/analytics/received?range=${receivedRange}`);
      setReceivedData(data);
    } catch (_e) { /* ignore */ }
  }, [receivedRange]);

  // Secondary Admin's Home also needs a WEEK card alongside TODAY.
  const loadReceivedWeek = useCallback(async () => {
    if (user?.role !== "secondary_admin") return;
    try {
      const { data } = await api.get(`/analytics/received?range=week`);
      setReceivedWeek(data);
    } catch (_e) { /* ignore */ }
  }, [user?.role]);

  // Secondary Admin also gets a 7-day sales chart on Home.
  const loadRevenueTimelineWeek = useCallback(async () => {
    if (user?.role !== "secondary_admin") return;
    try {
      setRevenueTimelineWeek(await fetchDeptTimelines("week"));
    } catch (_e) { /* ignore */ }
  }, [user?.role]);

  useEffect(() => { loadOrders(); }, [loadOrders]);
  useEffect(() => {
    loadStats();
    const t = setInterval(loadStats, 20000);
    return () => clearInterval(t);
  }, [loadStats]);
  useEffect(() => { loadRevenueTimeline(); }, [loadRevenueTimeline]);
  useEffect(() => { loadReceived(); }, [loadReceived]);
  useEffect(() => { loadReceivedWeek(); }, [loadReceivedWeek]);
  useEffect(() => { loadRevenueTimelineWeek(); }, [loadRevenueTimelineWeek]);

  const goToOrders = (stageKey = "all") => {
    setStage(stageKey);
    setSection("orders");
    setSidebarOpen(false);
  };

  const onCreated = async () => {
    setSection("orders");
    setStage("all");
    await loadOrders();
    await loadStats();
  };
  const onUpdated = async (opts = {}) => {
    // If a caller (e.g. OrderCard bypass) tells us to auto-switch tabs, do it
    // BEFORE we refresh — so the refreshed data lands on the correct filter.
    if (opts && opts.goTo && opts.goTo !== stage) {
      setStage(opts.goTo);
      // loadOrders is memoised on `stage`; the useEffect will pick this up
      // and refetch once state has committed. Still refresh stats/timeline.
      await loadStats();
      await loadRevenueTimeline();
      await loadReceived();
      return;
    }
    await loadOrders();
    await loadStats();
    await loadRevenueTimeline();
    await loadReceived();
  };

  const thisMonth = monthly[monthly.length - 1];
  const lastMonth = monthly[monthly.length - 2];
  const revDelta = thisMonth && lastMonth
    ? (lastMonth.revenue === 0 ? 100 : ((thisMonth.revenue - lastMonth.revenue) / lastMonth.revenue) * 100)
    : 0;

  return (
    <div className="min-h-screen flex bg-slate-900">
      <Sidebar
        active={section}
        onSelect={(k) => { setSection(k); setSidebarOpen(false); }}
        user={user}
        onLogout={() => { lockAllSections(); logout(); }}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        orderCount={overview?.counts_by_status?.new_order || 0}
      />

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="border-b border-slate-700/70 bg-slate-900/80 backdrop-blur sticky top-0 z-20">
          <div className="px-4 lg:px-10 py-4 flex items-center gap-3 flex-wrap">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-300 p-1" data-testid="sidebar-open">
              <List size={22} />
            </button>
            <div className="flex-1 min-w-[200px] max-w-xl">
              <GlobalSearch onOpen={setOpenId} />
            </div>
            {["admin", "secondary_admin"].includes(user?.role) && (
              <div className="order-last w-full md:order-none md:w-auto md:flex-1 md:min-w-[240px] md:max-w-sm">
                <HeyShobhit onNavigate={setSection} />
              </div>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <ThemeToggle />
              <div className="hidden sm:inline-flex"><InstallAppButton /></div>
              <div className="sm:hidden"><InstallAppButton compact /></div>
              <Button onClick={() => setGenerateQrOpen(true)} size="icon" variant="outline" aria-label="Generate QR"
                title="UPI QR banake customer se paisa mangwao"
                className="rounded-sm h-10 w-10 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700/60" data-testid="quick-generate-qr-button">
                <QrCode size={18} />
              </Button>
              <Button onClick={() => { setQuickPayOrderId(null); setQuickPayOpen(true); }} variant="outline"
                className="rounded-sm h-10 px-4 border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-700/60 font-medium" data-testid="quick-add-payment-button">
                <CurrencyInr size={15} className="sm:mr-1" weight="bold" /><span className="hidden sm:inline">Add payment</span>
              </Button>
              <Button onClick={() => setSection("new")} className="btn-industrial rounded-sm h-10 px-4" data-testid="quick-new-order-button">
                <Plus size={15} className="sm:mr-1" weight="bold" /><span className="hidden sm:inline">New order</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Main area */}
        <main className="flex-1 px-4 lg:px-10 py-6 lg:py-8">
          {section === "home" && (
            <>
              <HomeDashboard
                user={user}
                overview={overview}
                today={today}
                revenueTimeline={revenueTimeline}
                revenueTimelineWeek={revenueTimelineWeek}
                receivedRange={receivedRange}
                setReceivedRange={setReceivedRange}
                receivedData={receivedData}
                receivedWeek={receivedWeek}
                onStageClick={goToOrders}
                onOpenOrder={setOpenId}
                onSalesBucketClick={setSalesBucket}
                onReceivablesClick={() => setReceivablesOpen(true)}
                onReceivedClick={() => setReceivedDialogOpen(true)}
              />
              <MyTasksBanner
                includeUnassigned
                className="mt-6"
                onOpenAll={() => setSection("tasks")}
              />
            </>
          )}
          {(section === "orders" || section === "gates") && (
            <OrdersView
              orders={orders}
              stage={stage}
              setStage={setStage}
              onOpen={setOpenId}
              onQuickPayment={(id) => { setQuickPayOrderId(id); setQuickPayOpen(true); }}
              onChanged={onUpdated}
              title={section === "gates" ? "GATES & RAILINGS" : "ORDERS"}
              subtitle={section === "gates" ? "Long-duration fabrication projects (~1 month)" : null}
            />
          )}
          {section === "new" && (
            <div className="max-w-[1400px]">
              <NewOrderForm onCreated={onCreated} onBack={() => setSection("orders")} />
            </div>
          )}
          {section === "settings" && (
            (user?.role === "secondary_admin" || user?.role === "accountant") ? (
              <RestrictedSection label="Settings" onBack={() => setSection("home")} />
            ) : (
              <PinLock sectionKey="settings" sectionLabel="Settings" onCancel={() => setSection("home")}><SettingsPage /></PinLock>
            )
          )}
          {section === "reports" && (
            (user?.role === "secondary_admin" || user?.role === "accountant") ? (
              <RestrictedSection label="Reports" onBack={() => setSection("home")} />
            ) : (
              <PinLock sectionKey="reports" sectionLabel="Reports" onCancel={() => setSection("home")}><ReportsPage /></PinLock>
            )
          )}
          {section === "operators" && (
            (user?.role === "secondary_admin" || user?.role === "accountant") ? (
              <RestrictedSection label="Operators" onBack={() => setSection("home")} />
            ) : (
              <PinLock sectionKey="operators" sectionLabel="Operators" onCancel={() => setSection("home")}>
                <OperatorsPage onOpenOrder={setOpenId} />
              </PinLock>
            )
          )}
          {section === "recycle" && (
            (user?.role === "secondary_admin" || user?.role === "accountant") ? (
              <RestrictedSection label="Recycle Bin" onBack={() => setSection("home")} />
            ) : (
              <PinLock sectionKey="recycle" sectionLabel="Recycle Bin" onCancel={() => setSection("home")}><RecycleBinPage /></PinLock>
            )
          )}
          {section === "estimates" && <EstimatesPage />}
          {section === "tasks" && <TasksPage />}
          {section === "staff" && (
            <StaffPayrollPage />
          )}
          {section === "items" && <ItemsPage />}
          {section === "welders" && <WeldersPage />}
          {section === "fabrication" && <FabricationOverviewPage onOpenOrder={setOpenId} />}
          {section === "parties" && <PartiesPage onOpenOrder={setOpenId} />}
          {section === "payments" && <PaymentsPage />}
          {section === "daily_expenses" && <DailyExpensesPage />}
          {section === "commissions" && <CommissionPage />}
          {section === "designs" && <DesignsPage />}
          {section === "dues" && <DuesPage />}
          {section === "theme" && <ThemePage />}
          {section === "welder_commissions" && <WelderCommissionsPage />}
          {section === "accounts" && (
            <AccountsPage />
          )}
          {section === "delivery" && <DeliveryPanel embedded />}
        </main>
      </div>

      <OrderDetailDialog
        orderId={openId}
        open={!!openId}
        onClose={() => setOpenId(null)}
        onUpdated={onUpdated}
        isAdmin={true}
      />

      <QuickPaymentDialog
        open={quickPayOpen}
        onClose={() => { setQuickPayOpen(false); setQuickPayOrderId(null); }}
        onSaved={onUpdated}
        prefillOrderId={quickPayOrderId}
      />

      <GenerateQrDialog
        open={generateQrOpen}
        onClose={() => setGenerateQrOpen(false)}
      />

      <SalesOrdersDialog
        open={!!salesBucket}
        bucket={salesBucket}
        onClose={() => setSalesBucket(null)}
        onOpenOrder={setOpenId}
      />

      <ReceivablesDialog
        open={receivablesOpen}
        onClose={() => setReceivablesOpen(false)}
        onOpenOrder={setOpenId}
      />

      <ReceivedPaymentsDialog
        open={receivedDialogOpen}
        range={receivedRange}
        onClose={() => setReceivedDialogOpen(false)}
        onOpenOrder={setOpenId}
      />
    </div>
  );
}

/* ---------------- HOME (dashboard) ---------------- */
const RECEIVED_RANGE_OPTIONS = [
  { value: "today", label: "TODAY" },
  { value: "this_month", label: "THIS MONTH" },
  { value: "last_month", label: "LAST MONTH" },
  { value: "quarter", label: "QUARTER (3 MO)" },
  { value: "half_year", label: "HALF YEAR (6 MO)" },
  { value: "year", label: "YEAR (12 MO)" },
];

function ReceivedRangeMenu({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const btnRef = React.useRef(null);
  const current = RECEIVED_RANGE_OPTIONS.find((o) => o.value === value) || RECEIVED_RANGE_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const t = setTimeout(() => document.addEventListener("click", close, { once: true }), 0);
    return () => { clearTimeout(t); document.removeEventListener("click", close); };
  }, [open]);

  const toggle = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setRect({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setOpen((v) => !v);
  };

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className="flex items-center gap-1 text-[10px] font-mono-num tracking-widest text-slate-300 bg-slate-800/60 border border-slate-700 hover:border-emerald-500/50 hover:text-emerald-300 px-2 py-1 rounded-sm"
        data-testid="received-range-trigger"
        title="Change time range"
      >
        <span>{current.label}</span>
        <CaretDown size={10} weight="bold" />
      </button>
      {open && rect && createPortal(
        <div
          className="fixed z-[100] min-w-[200px] max-h-[70vh] overflow-y-auto bg-slate-900 border border-slate-700 rounded-sm shadow-xl"
          style={{ top: rect.top, right: rect.right }}
          onClick={(e) => e.stopPropagation()}
          data-testid="received-range-menu"
        >
          {RECEIVED_RANGE_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={
                "block w-full text-left text-[11px] font-mono-num tracking-widest px-3 py-2 hover:bg-slate-800 " +
                (o.value === value ? "text-emerald-300 bg-slate-800/60" : "text-slate-300")
              }
              data-testid={`received-range-${o.value}`}
            >
              {o.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

const DEPTS = [
  { key: "sheet_cutting", label: "SHEET CUTTING", tone: "border-orange-500", text: "text-orange-300" },
  { key: "gate_railing", label: "GATE & RAILING", tone: "border-cyan-400", text: "text-cyan-300" },
];

async function fetchDeptTimelines(range) {
  const [sheet, gate] = await Promise.all(DEPTS.map((d) =>
    api.get(`/analytics/revenue-timeline?range=${range}&metric=sales&project_type=${d.key}`).then((r) => r.data)));
  return { sheet_cutting: sheet, gate_railing: gate };
}

function DeptSalesChart({ dept, title, data, granularity, onBucketClick }) {
  return (
    <div className={`surface p-5 rounded-sm border-l-4 ${dept.tone}`} data-testid={`sales-chart-${dept.key}`}>
      <div className={`text-xs font-mono-num tracking-widest ${dept.text}`}>// {title}</div>
      <div className="mt-2 mb-4 flex items-baseline gap-3 flex-wrap">
        <span className="font-heading text-3xl font-black text-white font-mono-num" data-testid={`sales-total-${dept.key}`}>
          ₹{(data?.total_sales ?? 0).toLocaleString("en-IN")}
        </span>
        <span className="text-xs font-mono-num text-slate-500">{data?.total_orders ?? 0} orders in range</span>
        <span className="text-[10px] font-mono-num text-slate-500 tracking-widest hidden sm:inline">· CLICK BAR FOR DETAILS</span>
      </div>
      <RevenueTimelineChart
        data={data?.buckets || []}
        granularity={granularity}
        metric="sales"
        onBucketClick={(b) => onBucketClick?.({ ...b, project_type: dept.key, dept_label: dept.label })}
      />
    </div>
  );
}

function HomeView({ user, overview, today, monthly, leaderboard, thisMonth, revDelta, revenueRange, setRevenueRange, revenueTimeline, revenueTimelineWeek, receivedRange, setReceivedRange, receivedData, receivedWeek, onStageClick, onOpenOrder, onNewOrder, onSalesBucketClick, onReceivablesClick, onReceivedClick }) {
  const isSecondary = user?.role === "secondary_admin" || user?.role === "accountant";
  const isSecOnly = user?.role === "secondary_admin";   // WEEK card only for Sec Admin
  return (
    <div className="grid xl:grid-cols-[1fr_320px] gap-5">
      <div className="min-w-0 space-y-5">
        {/* Hero cards
             — Admin/Accountant: Total Receivable + Received (with range menu)
             — Secondary Admin: Today card + This-Week card (no Total Sale) */}
        <div className={isSecOnly
          ? "grid grid-cols-1 md:grid-cols-2 gap-4"
          : (isSecondary ? "grid grid-cols-1 gap-4" : "grid grid-cols-1 md:grid-cols-2 gap-4")}>
          {!isSecondary && (
            <HeroCard
              testid="hero-receivable"
              label="Total Receivable"
              hint={overview ? `From ${overview.receivable_from_parties} customers · Tap to see who` : ""}
              amount={overview?.total_receivable ?? 0}
              tone="orange"
              icon={<CurrencyInr size={22} />}
              onClick={onReceivablesClick}
              action={{ label: "View open orders", onClick: () => onStageClick("new_order") }}
            />
          )}
          <HeroCard
            testid={isSecOnly ? "hero-received-today" : "hero-received-month"}
            label={isSecOnly ? "Today's Sale" :
              `Received · ${(RECEIVED_RANGE_OPTIONS.find((o) => o.value === receivedRange)?.label) || "TODAY"}`}
            hint={(receivedData?.label ? `${receivedData.label} · Tap amount to see all payments` : "Tap amount to see all payments")}
            amount={(isSecOnly ? (receivedData?.received) : receivedData?.received) ?? 0}
            tone="emerald"
            icon={<TrendUp size={22} />}
            delta={isSecOnly ? undefined : (receivedData?.delta_pct ?? revDelta)}
            onClick={onReceivedClick}
            rangeSelector={isSecondary ? null : <ReceivedRangeMenu value={receivedRange} onChange={setReceivedRange} />}
          />
          {isSecOnly && (
            <HeroCard
              testid="hero-received-week"
              label="This Week's Sale"
              hint={receivedWeek?.label ? `${receivedWeek.label}` : "Rolling 7 days"}
              amount={receivedWeek?.received ?? 0}
              tone="cyan"
              icon={<TrendUp size={22} />}
              onClick={onReceivedClick}
            />
          )}
        </div>

        {/* Status pipeline */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-mono-num tracking-widest text-slate-400">// ORDER PIPELINE</div>
              <div className="text-slate-200 text-lg">Click a card to jump straight to those orders</div>
            </div>
          </div>
          <StatusPipeline counts={overview?.counts_by_status} onStageClick={onStageClick} />
        </div>

        {/* 7-day Sales charts — SECONDARY ADMIN only (dept-wise) */}
        {isSecOnly && (
          <div className="space-y-4" data-testid="sec-week-chart">
            {DEPTS.map((d) => (
              <DeptSalesChart key={d.key} dept={d} title={`${d.label} · LAST 7 DAYS`}
                data={revenueTimelineWeek?.[d.key]} granularity="day" onBucketClick={onSalesBucketClick} />
            ))}
          </div>
        )}

        {/* Dept-wise Sales charts — hidden for Secondary Admin + Accountant */}
        {!isSecondary && (
        <div className="space-y-4" data-testid="dept-sales-charts">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs font-mono-num tracking-widest text-slate-400" data-testid="revenue-chart-title">
              // SALES · {RANGE_LABEL[revenueRange] || "THIS MONTH"} · DEPARTMENT-WISE
            </div>
            <div className="flex items-center gap-1 surface-2 p-1 rounded-sm flex-wrap" data-testid="revenue-range-tabs">
              {RANGE_OPTIONS.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setRevenueRange(r.value)}
                  className={
                    "px-3 py-1.5 rounded-sm text-[10px] font-mono-num tracking-widest transition-colors " +
                    (revenueRange === r.value
                      ? "bg-orange-500 text-white"
                      : "text-slate-400 hover:text-slate-200")
                  }
                  data-testid={`revenue-range-${r.value}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          {DEPTS.map((d) => (
            <DeptSalesChart key={d.key} dept={d}
              title={`${d.label} · ${RANGE_LABEL[revenueRange] || "THIS MONTH"}`}
              data={revenueTimeline?.[d.key]}
              granularity={revenueTimeline?.[d.key]?.granularity || "month"}
              onBucketClick={onSalesBucketClick} />
          ))}
        </div>
        )}

        {/* Quick reports */}
        <div className="surface p-5 rounded-sm">
          <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-4 flex items-center gap-2">
            <ClipboardText size={14} /> // QUICK LINKS
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <QuickLink icon={<Package size={18}/>} label="All Orders" onClick={() => onStageClick("all")} testid="quick-all-orders" />
            <QuickLink icon={<Fire size={18}/>} label="Ready to Cut" onClick={() => onStageClick("ready_to_cut")} testid="quick-ready" />
            <QuickLink icon={<UsersIcon size={18}/>} label={`${overview?.total_customers ?? 0} Customers`} onClick={() => onStageClick("all")} testid="quick-customers" />
            <QuickLink icon={<Plus size={18}/>} label="New Order" onClick={onNewOrder} accent testid="quick-new" />
          </div>
        </div>

        {/* Leaderboard */}
        <div className="surface p-5 rounded-sm">
          <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-4 flex items-center gap-2">
            <UsersIcon size={14} /> // TOP WELDERS (BY REVENUE)
          </div>
          {(leaderboard?.top_welders || []).length === 0 ? (
            <div className="text-slate-500 text-sm">
              No welder orders yet. Go to <b className="text-orange-400">Welders</b> to add fabricator clients.
            </div>
          ) : (
            <div className="space-y-2">
              {leaderboard.top_welders.map((w, i) => (
                <div key={w.id || w.mobile}
                  className="flex items-center gap-3 p-3 rounded-sm surface-2"
                  data-testid={`leaderboard-welder-${i}`}>
                  <div className={
                    "w-8 h-8 rounded-sm flex items-center justify-center font-heading font-black text-sm " +
                    (i === 0 ? "bg-orange-500 text-white"
                      : i === 1 ? "bg-cyan-500/20 border border-cyan-500/50 text-cyan-300"
                      : "bg-slate-800 border border-slate-700 text-slate-300")
                  }>
                    #{i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-100 font-semibold truncate">
                      {w.name}
                      {w.shop_name && <span className="text-slate-500 text-xs ml-2">· {w.shop_name}</span>}
                    </div>
                    <div className="text-[11px] font-mono-num text-slate-500">
                      {w.orders} orders · {w.mobile}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-num text-orange-400 font-bold">
                      ₹{w.revenue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </div>
                    {w.balance > 0 && (
                      <div className="text-[10px] font-mono-num text-slate-500">
                        ₹{w.balance.toLocaleString("en-IN", { maximumFractionDigits: 0 })} due
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right widgets column */}
      <aside className="space-y-4 min-w-0" data-testid="right-widgets">
        <WidgetCard
          label="Received Today"
          value={<span className="font-mono-num text-2xl text-white">₹{(overview?.received_today ?? 0).toLocaleString("en-IN")}</span>}
          tone="orange"
        />

        <div className="surface p-4 rounded-sm">
          <div className="text-[10px] font-mono-num tracking-widest text-slate-400 mb-3">
            // TODAY&apos;S COLLECTIONS
          </div>
          {(today?.collections_by_mode || []).length === 0 ? (
            <div className="text-slate-500 text-xs py-1">No collections yet.</div>
          ) : (
            (today?.collections_by_mode || []).map((m) => (
              <PaymentModeRow
                key={m.key}
                label={m.label}
                amt={m.total}
                icon={m.key === "cash" ? <Money size={14}/> : <Bank size={14}/>}
              />
            ))
          )}
          <div className="border-t border-slate-700 mt-3 pt-3 flex items-center justify-between">
            <span className="text-slate-300 font-mono-num text-xs tracking-widest">TOTAL</span>
            <span className="font-mono-num text-lg text-orange-400 font-bold">
              ₹{(today?.collections?.total ?? 0).toLocaleString("en-IN")}
            </span>
          </div>
        </div>

        <div className="surface p-4 rounded-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-mono-num tracking-widest text-slate-400 flex items-center gap-2">
              <Fire size={12}/> // ON MACHINE NOW
            </div>
            <span className="text-slate-500 text-xs font-mono-num">{today?.in_cutting_count ?? 0}</span>
          </div>
          {(today?.in_cutting || []).length === 0 ? (
            <div className="text-slate-500 text-xs">No jobs on the machine.</div>
          ) : (
            <div className="space-y-2">
              {today.in_cutting.slice(0, 5).map((o) => (
                <button
                  key={o.id}
                  onClick={() => onOpenOrder(o.id)}
                  className="w-full flex items-center justify-between p-2 rounded-sm bg-slate-800/60 hover:bg-slate-800 text-left"
                  data-testid={`widget-cutting-${o.order_number}`}
                >
                  <div className="min-w-0">
                    <div className="font-mono-num text-orange-400 text-sm">#{o.order_number}</div>
                    <div className="text-slate-200 text-xs truncate">{o.customer_name}</div>
                  </div>
                  <ArrowRight size={14} className="text-slate-500 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="surface p-4 rounded-sm">
          <div className="text-[10px] font-mono-num tracking-widest text-slate-400 mb-3">
            // AT A GLANCE
          </div>
          <StatRow label="Total Orders" value={overview?.total_orders ?? 0} />
          <StatRow label="Customers" value={overview?.total_customers ?? 0} />
          <StatRow label="Orders Today" value={today?.total_today ?? 0} />
          <StatRow label="Completed Today" value={today?.completed_today ?? 0} />
        </div>
      </aside>
    </div>
  );
}

function HeroCard({ testid, label, hint, amount, tone = "orange", icon, action, delta, onClick, rangeSelector }) {
  const toneMap = {
    orange: { text: "text-orange-400", bar: "before:bg-orange-500", bg: "from-orange-500/10" },
    emerald: { text: "text-emerald-400", bar: "before:bg-emerald-500", bg: "from-emerald-500/10" },
  };
  const t = toneMap[tone] || toneMap.orange;
  return (
    <div
      className={
        `surface p-5 rounded-sm relative overflow-hidden ` +
        `before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 ${t.bar} ` +
        `bg-gradient-to-br ${t.bg} to-transparent ` +
        (onClick ? "cursor-pointer hover:bg-slate-800/40 transition-colors" : "")
      }
      onClick={onClick}
      role={onClick ? "button" : undefined}
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[10px] font-mono-num tracking-widest text-slate-400 uppercase">{label}</div>
          <div className={`mt-2 font-heading text-3xl md:text-4xl font-black font-mono-num ${t.text}`}>
            ₹{(amount || 0).toLocaleString("en-IN")}
          </div>
          {hint && <div className="text-xs text-slate-500 font-mono-num mt-1">{hint}</div>}
        </div>
        <div className="flex items-start gap-2 shrink-0">
          {rangeSelector}
          <div className={`${t.text} opacity-90`}>{icon}</div>
        </div>
      </div>
      {(delta !== undefined && Number.isFinite(delta)) && (
        <div className="mt-3">
          <DeltaBadge delta={delta} label={rangeSelector ? "vs prev period" : "vs last month"} small />
        </div>
      )}
      {action && (
        <button
          onClick={(e) => { e.stopPropagation(); action.onClick?.(); }}
          className="mt-3 text-[11px] font-mono-num text-slate-300 hover:text-orange-400 tracking-widest inline-flex items-center gap-1"
        >
          {action.label} <ArrowRight size={12} />
        </button>
      )}
    </div>
  );
}

function WidgetCard({ label, value, tone = "orange" }) {
  const toneMap = {
    orange: "border-orange-500/40",
    cyan: "border-cyan-500/40",
    emerald: "border-emerald-500/40",
  };
  return (
    <div className={`surface p-4 rounded-sm border-t-2 ${toneMap[tone]}`}>
      <div className="text-[10px] font-mono-num tracking-widest text-slate-400 mb-1">{label.toUpperCase()}</div>
      {value}
    </div>
  );
}

function PaymentModeRow({ label, amt = 0, icon }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="flex items-center gap-2 text-slate-300">
        {icon}
        <span>{label}</span>
      </span>
      <span className="font-mono-num text-slate-200">₹{(amt || 0).toLocaleString("en-IN")}</span>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-slate-400 text-sm">{label}</span>
      <span className="font-mono-num text-slate-200 font-semibold">{value}</span>
    </div>
  );
}

function QuickLink({ icon, label, onClick, accent, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={
        "flex items-center gap-2 px-4 py-3 rounded-sm text-sm transition-colors " +
        (accent
          ? "bg-orange-500 hover:bg-orange-600 text-white"
          : "surface-2 hover:bg-slate-800 text-slate-200")
      }
    >
      {icon}
      <span className="flex-1 text-left font-medium">{label}</span>
      <ArrowRight size={14} className="opacity-60" />
    </button>
  );
}

function DeltaBadge({ delta, label, small }) {
  const positive = delta >= 0;
  return (
    <span
      className={
        (small ? "text-[10px] " : "text-xs ") +
        "font-mono-num inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border " +
        (positive
          ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
          : "text-red-400 border-red-500/40 bg-red-500/10")
      }
    >
      {positive ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}% {label}
    </span>
  );
}

/* ---------------- ORDERS ---------------- */
function OrdersView({ orders, stage, setStage, onOpen, onQuickPayment, onChanged, title, subtitle }) {
  const [view, setView] = useState("cards"); // cards | table

  const isGateSection = title === "GATES & RAILINGS";
  const activeTabs = isGateSection ? GATE_STAGE_TABS : STAGE_TABS;
  const stageDef = activeTabs.find((s) => s.v === stage) || STAGE_TABS.find((s) => s.v === stage);
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-mono-num tracking-widest text-slate-400">// {title || "ORDERS"}</div>
          <h1 className="font-heading text-3xl font-black text-white">
            {stage === "all" ? (title === "GATES & RAILINGS" ? "All Gate / Railing Orders" : "All Orders") : stageDef?.label}
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {subtitle || getStageHint(stage)}
          </p>
        </div>

        <div className="flex items-center gap-1 surface-2 p-1 rounded-sm">
          <button
            onClick={() => setView("cards")}
            className={
              "px-3 py-1.5 rounded-sm text-xs font-mono-num tracking-widest transition-colors " +
              (view === "cards" ? "bg-orange-500 text-white" : "text-slate-400 hover:text-slate-200")
            }
            data-testid="view-cards-button"
          >
            <GridFour size={14} className="inline mr-1" /> CARDS
          </button>
          <button
            onClick={() => setView("table")}
            className={
              "px-3 py-1.5 rounded-sm text-xs font-mono-num tracking-widest transition-colors " +
              (view === "table" ? "bg-orange-500 text-white" : "text-slate-400 hover:text-slate-200")
            }
            data-testid="view-table-button"
          >
            <ListBullets size={14} className="inline mr-1" /> TABLE
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="stage-tabs">
        {activeTabs.map((s) => (
          <button
            key={s.v}
            onClick={() => setStage(s.v)}
            className={
              "px-3 py-2 rounded-sm text-[11px] font-mono-num tracking-widest border transition-colors " +
              (stage === s.v
                ? "bg-orange-500 border-orange-500 text-white"
                : "bg-slate-800/60 border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-slate-200")
            }
            data-testid={`stage-tab-${s.v}`}
          >
            {s.label.toUpperCase()}
          </button>
        ))}
        <div className="ml-auto font-mono-num text-sm text-slate-400">
          {orders.length} orders
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="surface p-12 rounded-sm text-center">
          <div className="w-14 h-14 stripe-accent mx-auto mb-3" />
          <div className="font-heading text-xl text-white font-black">Nothing here yet</div>
          <div className="text-slate-400 mt-1 text-sm">
            {stage === "all"
              ? "Create your first order using the + NEW ORDER button up top."
              : "No orders in this stage right now."}
          </div>
        </div>
      ) : view === "cards" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {orders.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              onOpen={onOpen}
              onQuickPayment={onQuickPayment}
              onChanged={onChanged}
            />
          ))}
        </div>
      ) : (
        <OrderTable orders={orders} onOpen={onOpen} />
      )}
    </div>
  );
}

function getStageHint(stage) {
  return ({
    all: "Every order in the system. Filter by stage below.",
    new_order: "Freshly received order. Upload design & move to next step.",
    design_pending: "Order confirmed — time to upload the design. Tap 'UPLOAD DESIGN' on any card.",
    ready_to_cut: "Design approved & cutting file uploaded. Operator can start cutting.",
    in_cutting: "On the machine right now.",
    completed: "Cutting done. Collect the final payment when the customer picks up.",
    delivered: "Order closed — fully paid and delivered.",
  })[stage] || "";
}

function RestrictedSection({ label, onBack }) {
  return (
    <div className="max-w-lg mx-auto mt-16 surface p-8 rounded-sm text-center space-y-3" data-testid="restricted-section">
      <div className="text-xs font-mono-num tracking-widest text-slate-500">// ACCESS RESTRICTED</div>
      <div className="text-2xl font-heading font-bold text-orange-300">{label}</div>
      <div className="text-slate-400 text-sm">
        Ye section sirf primary Admin ke liye reserved hai. Agar aapko access chahiye,
        primary Admin se contact karein.
      </div>
      <button onClick={onBack} className="btn-industrial mt-2" data-testid="restricted-back">
        Home pe wapas jayein
      </button>
    </div>
  );
}

